import {
  MouseEvent,
  ReactNode,
  Ref,
  forwardRef,
  useImperativeHandle,
  useState,
} from "react";
import { FormattedMessage } from "react-intl";
import { graphql, usePaginationFragment } from "react-relay";
import { ChevronDownIcon } from "@/icons";
import Button from "./Button";
import Dropdown from "./Dropdown";
import { RelativeTime } from "./RelativeTime";
import { CompetitionStageSelectFragment$key } from "./__generated__/CompetitionStageSelectFragment.graphql";
import { useOnChangeEffect } from "@/utils/hooks";
import Loading from "./Loading";

const Fragment = graphql`
  fragment CompetitionStageSelectFragment on Competition
  @refetchable(queryName: "CompetitionStageSelectFragmentPaginationQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
    hasLeaderboard: { type: "Boolean" }
    requiresApproval: { type: "Boolean" }
  ) {
    currentStage {
      id
      seq
    }

    stages(
      first: $count
      after: $cursor
      hasLeaderboard: $hasLeaderboard
      requiresApproval: $requiresApproval
    ) @connection(key: "CompetitionStageSelectFragment_stages") {
      edges {
        node {
          id
          seq
          validUntil
        }
      }
    }
  }
`;

export interface StageShape {
  id: ID;
  seq: number;
}

export interface StageFullShape extends StageShape {
  validUntil: string | undefined | null;
}

export interface BaseCompetitionStageSelectProps {
  stages: StageFullShape[];
  currentStage?: StageShape;
  value?: number;
  defaultValue?: number | null;
  disabled?: boolean;
  isLoading?: boolean;
  placeholder?: ReactNode;
  onChange?: (stage: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  loadNext?: () => void;
  hasNext?: boolean;
  isLoadingNext?: boolean;
}

export interface CompetitionStageSelectProps extends Omit<
  BaseCompetitionStageSelectProps,
  "stages" | "currentStage" | "loadNext" | "hasNext" | "isLoadingNext"
> {
  competition: CompetitionStageSelectFragment$key;
}

export interface CompetitionStageSelectHandle {
  focus(): void;
  blur(): void;
}

export default forwardRef(function CompetitionStageSelect(
  { competition, ...props }: CompetitionStageSelectProps,
  ref: Ref<CompetitionStageSelectHandle>,
) {
  const {
    data: {
      currentStage,
      stages: { edges },
    },
    loadNext,
    hasNext,
    isLoadingNext,
  } = usePaginationFragment(Fragment, competition);

  return (
    <BaseCompetitionStageSelect
      ref={ref}
      {...props}
      stages={edges.map(({ node }) => node)}
      currentStage={currentStage}
      loadNext={() => loadNext(10)}
      hasNext={hasNext}
      isLoadingNext={isLoadingNext}
    />
  );
});

export const BaseCompetitionStageSelect = forwardRef(
  function BaseCompetitionStageSelect(
    {
      stages,
      currentStage,
      value,
      defaultValue,
      disabled,
      isLoading,
      placeholder,
      onChange,
      onFocus,
      onBlur,
      loadNext,
      hasNext = false,
      isLoadingNext = false,
    }: BaseCompetitionStageSelectProps,
    ref: Ref<CompetitionStageSelectHandle>,
  ) {
    const [isOpen, setOpen] = useState(false);
    defaultValue =
      defaultValue !== null
        ? defaultValue
        : currentStage && stages.some((stage) => stage.id === currentStage.id)
          ? currentStage.seq
          : undefined;
    const [selectedStageSeq, setSelectedStageSeq] = useState(
      value ?? defaultValue,
    );
    useOnChangeEffect(value, setSelectedStageSeq);

    useOnChangeEffect(stages, () => {
      console.log("changed:", stages);
    });

    useImperativeHandle(ref, () => ({
      focus() {
        if (stages.length > 1) {
          setOpen(true);
        }
      },
      blur() {
        setOpen(false);
      },
    }));

    if (stages.length <= 0) {
      return null;
    } else if (stages.length === 1) {
      if (currentStage?.seq === 1) {
        return null;
      }
      const stage = stages[0];
      if (stage.id === currentStage?.id) {
        return (
          <span>
            <CurrentStageLabel />
          </span>
        );
      } else {
        return (
          <span>
            <StageLabel stage={stages[0].seq} />
          </span>
        );
      }
    }

    const onOpenChange = (isOpen: boolean) => {
      setOpen(isOpen);
      if (isOpen) {
        onFocus?.();
      } else {
        onBlur?.();
      }
    };

    const onClickItem = (stage: number, _evt: MouseEvent) => {
      setSelectedStageSeq(stage);
      onChange?.(stage);
      onOpenChange(false);
    };

    return (
      <Dropdown
        open={isOpen}
        onOpenChange={onOpenChange}
        trigger={
          <Button
            kind="text"
            className="inline-flex row items-center h-9 rounded-lg border border-grey-300 px-3.5 py-2 focus:outline-hidden focus:ring-2 focus:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
            disabled={disabled}
          >
            {selectedStageSeq === currentStage?.seq ? (
              <CurrentStageLabel />
            ) : typeof selectedStageSeq !== "undefined" ? (
              <StageLabel stage={selectedStageSeq} />
            ) : (
              (placeholder ?? <FormattedMessage defaultMessage="Stages" />)
            )}

            {isLoading ? (
              <Loading className="ml-1 h-4 w-4 opacity-50" />
            ) : (
              <ChevronDownIcon className="ml-1 h-4 w-4 opacity-50" />
            )}
          </Button>
        }
      >
        {stages.map((stage) => (
          <Dropdown.Item
            key={stage.id}
            onClick={onClickItem.bind(null, stage.seq)}
            selected={stage.seq === selectedStageSeq}
          >
            {stage.id === currentStage?.id ? (
              <CurrentStageLabel />
            ) : (
              <StageLabel stage={stage.seq} />
            )}{" "}
            {stage.validUntil && (
              <FormattedMessage
                defaultMessage="(until {date})"
                values={{
                  date: <RelativeTime date={new Date(stage.validUntil)} />,
                }}
              />
            )}
          </Dropdown.Item>
        ))}

        {hasNext && (
          <Dropdown.Item onClick={loadNext} disabled={isLoadingNext}>
            <FormattedMessage defaultMessage="Previous stages..." />
          </Dropdown.Item>
        )}
      </Dropdown>
    );
  },
);

export const StageLabel = ({ stage }: { stage: number }) => (
  <FormattedMessage defaultMessage="Stage {stage}" values={{ stage }} />
);

export const CurrentStageLabel = () => (
  <FormattedMessage defaultMessage="Current stage" />
);
